(function initializeTulipAppointments() {
  const CONFIG_SCRIPT_ID = 'tulip-appointments-config';
  const SELECTOR_CONTAINER_ID = 'timekit-project-selector-container';
  const TITLE_SELECTOR = '.tulip-appointments-title';
  const HEADER_SELECTOR = '.timekit-header';
  const SEARCH_BAR_SELECTOR = '.search-bar-wrapper';
  const SUCCESS_FORM_SELECTOR = '.bookingjs-form.success';
  const NAVIGATION_TITLE_SELECTOR = '.timekit-navigation-title';
  const NAVIGATION_DESCRIPTION_SELECTOR = '.timekit-navigation-description';
  const BOOKED_EMAIL_SELECTOR =
    '#booked-email, .booked-email, [name="booked-email"], [data-field-key="booked-email"]';
  const STORE_PROJECT_STRATEGY = 'store_project';
  const APPOINTMENT_TYPE_PROJECT_STRATEGY = 'global_appointment_type_project';
  const STORE_PARAM = 'store';
  const STORE_CITY_PARAM = 'store_city';
  // The selector builds every card id as `${projectType}_id-card-${project.id}`.
  const STORE_CARD_SELECTOR = `[id^="${STORE_PROJECT_STRATEGY}_id-card-"]`;
  const STORE_CARD_TIMEOUT_MS = 5000;
  const SECTION_SELECTOR = '.tulip-appointments-section';
  const PRESELECTING_CLASS = 'tulip-appointments-section--preselecting';
  const ALLOWED_STORE_NAMES = [
    'Toronto',
    'Montreal',
    'Vancouver',
    'Boston',
    'Charlotte',
    'Bal Harbour',
    'Dallas',
    'Seattle',
    'Chicago',
    'Newport',
  ];

  const ALLOWED_STORE_TOKENS = ALLOWED_STORE_NAMES.map((storeName) =>
    storeName.toLowerCase().trim(),
  );

  /**
   * Accents are folded because the two systems disagree on them: Uberall's `Montréal` is
   * Timekit's `Montreal`. Mirrors normalizeStoreName in pickup-store-storage.js, which this
   * script cannot import because it is not loaded as a module.
   * @param {unknown} value
   * @returns {string}
   */
  const normalizeStoreValue = (value) =>
    typeof value === 'string'
      ? value
          .normalize('NFKD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .trim()
      : '';

  /**
   * @param {any} project
   * @returns {boolean}
   */
  const isAllowedStoreProject = (project) => {
    const candidateStoreValues = [
      project?.meta?.t_store_name,
      project?.name,
      project?.meta?.t_store_city,
    ]
      .map(normalizeStoreValue)
      .filter(Boolean);

    if (candidateStoreValues.length === 0) return false;

    return candidateStoreValues.some((candidate) =>
      ALLOWED_STORE_TOKENS.some((allowedStore) => candidate.includes(allowedStore)),
    );
  };

  /**
   * @param {any} tps
   */
  const installStoreWhitelistOverride = (tps) => {
    if (!tps || typeof tps.getStrategy !== 'function') return;
    if (tps._vbStoreWhitelistInstalled) return;

    /**
     * @param {any} strategy
     */
    const wrapStoreStrategyProjects = (strategy) => {
      if (!strategy || typeof strategy.getProjects !== 'function') return;
      if (strategy._vbStoreWhitelistInstalled) return;

      const originalGetProjects = strategy.getProjects.bind(strategy);
      /**
       * @param {...any} args
       */
      const getProjectsWithWhitelist = async (...args) => {
        const projects = await originalGetProjects(...args);
        if (!Array.isArray(projects)) return projects;
        return projects.filter(isAllowedStoreProject);
      };

      strategy.getProjects = getProjectsWithWhitelist;
      strategy._vbStoreWhitelistInstalled = true;
    };

    const originalGetStrategy = tps.getStrategy.bind(tps);
    /**
     * @param {string} strategyName
     */
    tps.getStrategy = (strategyName) => {
      const strategy = originalGetStrategy(strategyName);
      if (strategyName === STORE_PROJECT_STRATEGY) {
        wrapStoreStrategyProjects(strategy);
      }
      return strategy;
    };

    wrapStoreStrategyProjects(originalGetStrategy(STORE_PROJECT_STRATEGY));
    tps._vbStoreWhitelistInstalled = true;
  };

  /**
   * Store hints carried over from the store details page.
   * @returns {string[]}
   */
  const getStoreHintValues = () => {
    const params = new URLSearchParams(window.location.search);

    return [params.get(STORE_PARAM), params.get(STORE_CITY_PARAM)]
      .map(normalizeStoreValue)
      .filter(Boolean);
  };

  /**
   * Store name and city as the card renders them: card_body is address, city, then phone.
   * @param {HTMLElement} card
   * @returns {string[]}
   */
  const getCardMatchCandidates = (card) => {
    const cardName = card.querySelector('.card-title')?.textContent;
    const cardCity = (card.querySelector('.card-body')?.innerHTML || '')
      .split(/<br\s*\/?>/i)[1]
      ?.replace(/<[^>]+>/g, ' ');

    return [cardName, cardCity].map(normalizeStoreValue).filter(Boolean);
  };

  /**
   * @param {HTMLElement[]} cards
   * @param {string[]} hintValues
   * @returns {HTMLElement | undefined}
   */
  const findStoreCardByHint = (cards, hintValues) => {
    const exactMatch = cards.find((card) =>
      getCardMatchCandidates(card).some((candidate) => hintValues.includes(candidate)),
    );
    if (exactMatch) return exactMatch;

    return cards.find((card) =>
      getCardMatchCandidates(card).some((candidate) =>
        hintValues.some((hint) => candidate.includes(hint) || hint.includes(candidate)),
      ),
    );
  };

  /**
   * The store step paints after init resolves, and the observer batches the whole card loop
   * into one callback, so the cards are matched against as a complete set.
   * @param {HTMLElement} selectorContainer
   * @returns {Promise<HTMLElement[]>}
   */
  const waitForStoreCards = (selectorContainer) =>
    new Promise((resolve) => {
      const findCards = () =>
        /** @type {HTMLElement[]} */ (
          [...selectorContainer.querySelectorAll(STORE_CARD_SELECTOR)].filter(
            (card) => card instanceof HTMLElement,
          )
        );

      const existingCards = findCards();
      if (existingCards.length > 0) {
        resolve(existingCards);
        return;
      }

      let timeoutId = 0;

      const observer = new MutationObserver(() => {
        const cards = findCards();
        if (cards.length === 0) return;

        window.clearTimeout(timeoutId);
        observer.disconnect();
        resolve(cards);
      });

      observer.observe(selectorContainer, { childList: true, subtree: true });

      timeoutId = window.setTimeout(() => {
        observer.disconnect();
        resolve([]);
      }, STORE_CARD_TIMEOUT_MS);
    });

  /**
   * Skip the store step when the visitor arrived from a store page. Clicking the card reuses the
   * selector's own step transition, which `selectProject` would bypass by opening the calendar.
   * @param {HTMLElement} selectorContainer
   * @param {string[]} hintValues
   */
  const preselectStoreFromUrl = async (selectorContainer, hintValues) => {
    if (hintValues.length === 0) return;

    const cards = await waitForStoreCards(selectorContainer);
    findStoreCardByHint(cards, hintValues)?.click();
  };

  const getConfig = () => {
    const configScript = document.getElementById(CONFIG_SCRIPT_ID);
    if (!configScript) return null;

    try {
      return JSON.parse(configScript.textContent || '{}');
    } catch {
      return null;
    }
  };

  /**
   * @param {any} tps
   */
  const installStepTracking = (tps) => {
    if (typeof tps.getStepsFactory !== 'function') return;

    const stepsFactory = tps.getStepsFactory();
    if (!stepsFactory || stepsFactory._vbStepTrackingInstalled) return;

    const getStepNumber = () => {
      const steps = typeof stepsFactory.getSteps === 'function' ? stepsFactory.getSteps() : [];
      const activeStep =
        typeof stepsFactory.currentStep === 'function' ? stepsFactory.currentStep() : null;
      const activeStepIndex = steps.indexOf(activeStep);

      return activeStepIndex >= 0 ? activeStepIndex + 1 : null;
    };

    const updateStepNumber = () => {
      const stepNumber = getStepNumber();
      if (!stepNumber || stepNumber === stepsFactory._vbLastTrackedStepNumber) return;

      stepsFactory._vbLastTrackedStepNumber = stepNumber;

      const selectorContainer = document.getElementById(SELECTOR_CONTAINER_ID);
      if (selectorContainer) {
        selectorContainer.dataset.currentStepNumber = String(stepNumber);
      }

      const title = document.querySelector(TITLE_SELECTOR);
      if (title) {
        title.classList.toggle('tulip-appointments-title--hidden', stepNumber !== 1);
      }

      const timekitHeaders = document.querySelectorAll(HEADER_SELECTOR);
      timekitHeaders.forEach((header) => {
        if (header instanceof HTMLElement) {
          header.style.display = stepNumber > 1 ? '' : 'none';
        }
      });

      const searchBars = document.querySelectorAll(SEARCH_BAR_SELECTOR);
      searchBars.forEach((bar) => {
        bar.classList.toggle('search-bar-wrapper--visible', stepNumber > 1);
      });
    };

    /**
     * @param {string} methodName
     */
    const wrapStepMethod = (methodName) => {
      if (typeof stepsFactory[methodName] !== 'function') return;

      const originalMethod = stepsFactory[methodName].bind(stepsFactory);
      /**
       * @param {...any} args
       */
      const wrappedMethod = (...args) => {
        const result = originalMethod(...args);
        updateStepNumber();
        return result;
      };
      stepsFactory[methodName] = wrappedMethod;
    };

    wrapStepMethod('nextStep');
    wrapStepMethod('previousStep');
    stepsFactory._vbStepTrackingInstalled = true;
    updateStepNumber();
  };

  /**
   * @param {Element | { value?: string; textContent?: string } | null} element
   */
  const getTextOrValue = (element) => {
    if (!element) return '';
    if ('value' in element && typeof element.value === 'string') return element.value.trim();
    return (element.textContent || '').trim();
  };

  /**
   * @param {Element | null} cardBodyElement
   * @returns {string}
   */
  const extractAddressFromCardBody = (cardBodyElement) => {
    if (!cardBodyElement) return '';

    // Store cards render address as first segment followed by <br> city/phone lines.
    const firstLineFromHtml = ((cardBodyElement.innerHTML || '').split(/<br\s*\/?>/i)[0] || '')
      .replace(/<[^>]+>/g, ' ')
      .trim();

    if (firstLineFromHtml) return firstLineFromHtml;

    const lines = (cardBodyElement.textContent || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length === 0) return '';

    return lines[0] || '';
  };

  /**
   * @param {string} address
   * @returns {string}
   */
  const buildDirectionsUrl = (address) => {
    if (!address) return '';
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  /**
   * @param {string} template
   * @param {string} email
   * @returns {string}
   */
  const formatSuccessDescription = (template, email) => {
    if (!template) return '';
    return template
      .replace(/__EMAIL__/g, email || '')
      .replace(/\{\{\s*email\s*\}\}/gi, email || '');
  };

  /**
   * @param {HTMLElement} selectorContainer
   * @returns {string}
   */
  const getSelectedStoreAddress = (selectorContainer) => {
    const previewCardBodies = selectorContainer.querySelectorAll(
      '.user-selection-preview .card-body',
    );
    if (previewCardBodies.length > 0) {
      const lastPreviewCardBody = previewCardBodies[previewCardBodies.length - 1] || null;
      const previewAddress = extractAddressFromCardBody(lastPreviewCardBody);
      if (previewAddress) return previewAddress;
    }

    const selectedCardBody = selectorContainer.querySelector(
      '.selector-card-container .selected .card-body, .selector-card-container .active .card-body',
    );
    if (selectedCardBody) {
      const selectedAddress = extractAddressFromCardBody(selectedCardBody);
      if (selectedAddress) return selectedAddress;
    }

    return '';
  };

  /**
   * @param {HTMLElement} selectorContainer
   * @param {{
   *   successHeading?: string,
   *   successDescriptionTemplate?: string,
   *   appointmentDetailsTitle?: string,
   *   getDirectionsLabel?: string
   * }} config
   */
  const installSuccessStateEnhancements = (selectorContainer, config) => {
    if (selectorContainer.dataset.successEnhancementsInstalled === 'true') return;

    let bookedEmail = '';
    let selectedStoreAddress = '';
    let successStateApplied = false;

    /**
     * @param {ParentNode} [root]
     */
    const updateBookedEmail = (root = selectorContainer) => {
      const emailField = root.querySelector(BOOKED_EMAIL_SELECTOR);
      const emailValue = getTextOrValue(emailField);
      if (emailValue) {
        bookedEmail = emailValue;
      }
    };

    const updateSelectedStoreAddress = () => {
      const selectedAddress = getSelectedStoreAddress(selectorContainer);
      if (selectedAddress) {
        selectedStoreAddress = selectedAddress;
      }
    };

    const updateNavigationCopy = () => {
      const navigationTitle = selectorContainer.querySelector(NAVIGATION_TITLE_SELECTOR);
      if (
        navigationTitle &&
        config.successHeading &&
        navigationTitle.textContent !== config.successHeading
      ) {
        navigationTitle.textContent = config.successHeading;
      }

      const navigationDescription = selectorContainer.querySelector(
        NAVIGATION_DESCRIPTION_SELECTOR,
      );
      if (navigationDescription && config.successDescriptionTemplate) {
        const successDescription = formatSuccessDescription(
          config.successDescriptionTemplate,
          bookedEmail,
        );
        if (navigationDescription.textContent !== successDescription) {
          navigationDescription.textContent = successDescription;
        }
      }
    };

    /**
     * @param {HTMLElement} successForm
     */
    const addSuccessDetails = (successForm) => {
      if (
        !config.appointmentDetailsTitle ||
        !config.getDirectionsLabel ||
        successForm.querySelector('.tulip-success-details')
      )
        return;

      const detailsContainer = document.createElement('div');
      detailsContainer.className = 'tulip-success-details';

      const detailsTitle = document.createElement('h3');
      detailsTitle.className = 'tulip-success-details__title';
      detailsTitle.textContent = config.appointmentDetailsTitle;
      detailsContainer.appendChild(detailsTitle);

      const directionsButton = document.createElement('button');
      directionsButton.type = 'button';
      directionsButton.className = 'button button-unstyled tulip-success-directions-button';
      directionsButton.textContent = config.getDirectionsLabel;

      const mapsUrl = buildDirectionsUrl(selectedStoreAddress);
      if (mapsUrl) {
        directionsButton.addEventListener('click', () => {
          window.open(mapsUrl, '_blank', 'noopener,noreferrer');
        });
      } else {
        directionsButton.disabled = true;
      }

      detailsContainer.appendChild(directionsButton);
      successForm.appendChild(detailsContainer);
    };

    /**
     * Move booking details into the success details container so layout order is explicit.
     * @param {HTMLElement} successForm
     */
    const reorderSuccessLayout = (successForm) => {
      const detailsContainer = successForm.querySelector('.tulip-success-details');
      if (!(detailsContainer instanceof HTMLElement)) return;

      if (detailsContainer.dataset.reordered === 'true') return;

      const directionsButton = detailsContainer.querySelector('.tulip-success-directions-button');
      if (!(directionsButton instanceof HTMLElement)) return;

      const bookpage = successForm.closest('.bookingjs-bookpage');
      if (!(bookpage instanceof HTMLElement)) return;

      const dateElement = bookpage.querySelector('.bookingjs-bookpage-date');
      if (dateElement) {
        detailsContainer.insertBefore(dateElement, directionsButton);
      }

      const timeElement = bookpage.querySelector('.bookingjs-bookpage-time');
      const resourceElement = bookpage.querySelector('.bookingjs-bookpage-resource');

      if (timeElement || resourceElement) {
        const timeResourceRow = document.createElement('div');
        timeResourceRow.className = 'tulip-success-time-resource';

        if (timeElement) {
          timeResourceRow.appendChild(timeElement);
        }

        if (resourceElement) {
          timeResourceRow.appendChild(resourceElement);
        }

        detailsContainer.insertBefore(timeResourceRow, directionsButton);
      }

      detailsContainer.dataset.reordered = 'true';
    };

    const enhanceSuccessState = () => {
      updateBookedEmail();
      updateSelectedStoreAddress();

      const successForms = selectorContainer.querySelectorAll(SUCCESS_FORM_SELECTOR);
      if (successForms.length === 0) return false;

      updateNavigationCopy();
      successForms.forEach((successForm) => {
        if (successForm instanceof HTMLElement) {
          addSuccessDetails(successForm);
          reorderSuccessLayout(successForm);
        }
      });

      return true;
    };

    /**
     * @param {Event} event
     */
    const onEmailInput = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.matches(BOOKED_EMAIL_SELECTOR)) return;
      const inputValue = getTextOrValue(target);
      if (inputValue) {
        bookedEmail = inputValue;
      }
    };

    /**
     * @param {Event} event
     */
    const onStoreSelection = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const currentStepNumber = Number(selectorContainer.dataset.currentStepNumber || '0');
      if (currentStepNumber !== 2) return;

      const cardContainer = target.closest('.card-container, .card-container-image');
      if (!cardContainer) return;

      const cardBody = cardContainer.querySelector('.card-body');
      const selectedAddress = extractAddressFromCardBody(cardBody);
      if (selectedAddress) {
        selectedStoreAddress = selectedAddress;
      }
    };

    const observer = new MutationObserver(() => {
      if (successStateApplied) return;

      const didApplySuccessState = enhanceSuccessState();
      if (didApplySuccessState) {
        successStateApplied = true;
        observer.disconnect();
      }
    });

    observer.observe(selectorContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    selectorContainer.addEventListener('input', onEmailInput, true);
    selectorContainer.addEventListener('change', onEmailInput, true);
    selectorContainer.addEventListener('click', onStoreSelection, true);

    selectorContainer.dataset.successEnhancementsInstalled = 'true';
    successStateApplied = enhanceSuccessState();
    if (successStateApplied) {
      observer.disconnect();
    }
  };

  const initialize = async () => {
    const config = getConfig();
    const selectorContainer = document.getElementById(SELECTOR_CONTAINER_ID);
    const tps = /** @type {any} */ (window).timekit_project_selector;

    if (!config || !config.appKey || !selectorContainer || !tps || typeof tps.init !== 'function')
      return;

    installStoreWhitelistOverride(tps);

    // Hidden up front so a deep-linked visitor never watches the store step paint and vanish.
    const section = selectorContainer.closest(SECTION_SELECTOR);
    const storeHintValues = getStoreHintValues();
    if (storeHintValues.length > 0) section?.classList.add(PRESELECTING_CLASS);

    try {
      await tps.init({
        embed: true,
        defaultUI: true,
        includePrivateAppointments: true,
        api_base_url: 'https://api.timekit.io',
        app_key: config.appKey,
        shouldConsiderAssociateAvailability: true,
        selectorOptions: {
          [STORE_PROJECT_STRATEGY]: {
            title: config.selectStoreTitle,
            description: config.selectStoreDescription,
            card_title: '[meta]t_store_name',
            card_body:
              '{{[meta]t_store_address}}<br>{{[meta]t_store_city}}<br>{{[meta]t_store_phone}}',
            card_footer: `<button class="button button-unstyled">${config.selectLabel}</button>`,
            search_bar: {
              enabled: true,
              placeholder: config.searchForCityPlaceholder,
            },
            strategy: STORE_PROJECT_STRATEGY,
          },
          [APPOINTMENT_TYPE_PROJECT_STRATEGY]: {
            title: config.selectAppointmentTypeTitle,
            description: config.selectAppointmentTypeDescription,
            card_image: '[meta]t_appointment_type_image',
            card_title: '[meta]t_appointment_type_name',
            card_body: '[meta]t_appointment_type_description',
            card_footer: `<button class="button button-unstyled">${config.selectLabel}</button>`,
            filters: {
              t_disabled: 0,
              t_private: 0,
            },
          },
        },
      });

      selectorContainer.classList.add('open');
      installStepTracking(tps);
      installSuccessStateEnhancements(selectorContainer, config);
      await preselectStoreFromUrl(selectorContainer, storeHintValues);

      const headerRight = document.getElementById('timekit-header-right');
      if (!headerRight) return;

      headerRight.onclick = () => {
        const stylistChat = document.getElementById('stylist-chat');
        if (stylistChat) {
          stylistChat.classList.remove('disable-click');
        }

        const stylistChatText = document.getElementById('stylist-chat-text');
        if (stylistChatText) {
          stylistChatText.classList.remove('disable-click');
        }

        selectorContainer.classList.remove('open');

        const floatingButton = document.getElementById('floating-button');
        if (floatingButton) {
          floatingButton.style.display = 'none';
        }
      };
    } catch {
      // Fail open: if Timekit initialization fails, keep the section visible.
    } finally {
      section?.classList.remove(PRESELECTING_CLASS);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
    return;
  }

  initialize();
})();
